import {Directive, ElementRef, forwardRef, HostListener} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

const onlyLatinCyrillicKazakhSymbols = /[^a-zA-Zа-яА-ЯёЁәғқңөұүһіӘҒҚҢӨҰҮҺІ\s-]/g;
const removeHyphensAndSpacesAtStartEnd = /^[\s-]+|[\s-]+$/g;
const multipleSpaces = /\s+/g;
const multipleHyphens = /-+/g;

@Directive({
    selector: 'input[commonsNameFormatter]',
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => NameFormatterDirective),
        multi: true,
    }],
    standalone: false,
})
export class NameFormatterDirective implements ControlValueAccessor {
    private lastValidValue: string;
    private onChange!: (value: string) => void;
    private onTouched!: () => void;

    constructor(private el: ElementRef) {
    }

    @HostListener('input', ['$event.target'])
    onInput(target: HTMLInputElement): void {
        const original = target.value;
        const formatted = this.formatValue(original);
        const hasDelimiter = original.endsWith(' ') || original.endsWith('-');

        if (formatted !== original && !hasDelimiter) {
            const cursor = target.selectionStart ?? original.length;
            target.value = formatted;
            this.el.nativeElement.setSelectionRange(cursor, cursor);
        }
        this.lastValidValue = hasDelimiter ? original : formatted;
        this.onChange(this.lastValidValue);
        this.onTouched();
    }

    @HostListener('blur')
    onBlur(): void {
        const valueToUse = this.lastValidValue ?? this.el.nativeElement.value;
        const formatted = this.formatValue(valueToUse);

        this.onChange?.(formatted);
        this.onTouched?.();
        this.el.nativeElement.value = formatted;
    }

    writeValue(value: any): void {
        if (value !== undefined && value !== null) {
            this.el.nativeElement.value = this.formatValue(value);
        }
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.el.nativeElement.disabled = isDisabled;
    }

    private formatValue(value: string): string {
        if (!value) {
            return null;
        }
        value = value.replace(onlyLatinCyrillicKazakhSymbols, '');

        value = value.replace(removeHyphensAndSpacesAtStartEnd, '');
        value = value.replace(multipleSpaces, ' ');
        value = value.replace(multipleHyphens, '-');

        return value
            .split(' ')
            .map(word => word.split('-')
                .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
                .join('-'),
            )
            .join(' ');
    }
}
